import { useState, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Icon } from "@/components/ui/icon";
import { toast } from "sonner";
import { DraggableList } from "./DraggableList";
export const CompetenciesManager = () => {
  const queryClient = useQueryClient();
  const [competency, setCompetency] = useState("");
  const {
    data: competencies
  } = useQuery({
    queryKey: ["admin-competencies"],
    queryFn: async () => {
      const {
        data,
        error
      } = await supabase.from("core_competencies").select("*").order("display_order");
      if (error) throw error;
      return data;
    }
  });
  const addMutation = useMutation({
    mutationFn: async (competency: string) => {
      const {
        error
      } = await supabase.from("core_competencies").insert([{
        competency,
        display_order: (competencies?.length || 0) + 1
      }]);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin-competencies"]
      });
      queryClient.invalidateQueries({
        queryKey: ["competencies"]
      });
      toast.success("Competency added successfully");
      setCompetency("");
    }
  });
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const {
        error
      } = await supabase.from("core_competencies").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin-competencies"]
      });
      queryClient.invalidateQueries({
        queryKey: ["competencies"]
      });
      toast.success("Competency deleted successfully");
    }
  });
  const reorderMutation = useMutation({
    mutationFn: async (items: typeof competencies) => {
      if (!items) return;
      for (let i = 0; i < items.length; i++) {
        const {
          error
        } = await supabase.from("core_competencies").update({
          display_order: i
        }).eq("id", items[i].id);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin-competencies"]
      });
      queryClient.invalidateQueries({
        queryKey: ["competencies"]
      });
      toast.success("Order updated");
    }
  });
  const handleReorder = useCallback((newItems: NonNullable<typeof competencies>) => {
    reorderMutation.mutate(newItems);
  }, [reorderMutation]);
  return <div className="space-y-6">
      <Card className="p-6 bg-card border-border shadow-sm rounded-xl">
        <h2 className="text-2xl font-bold text-foreground mb-6">Add Core Competency</h2>
        <form onSubmit={e => {
        e.preventDefault();
        addMutation.mutate(competency);
      }} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="competency">Competency *</Label>
            <Input id="competency" value={competency} onChange={e => setCompetency(e.target.value)} required />
          </div>

          <Button type="submit" disabled={addMutation.isPending} className="w-full">
            {addMutation.isPending ? "Adding..." : "Add Competency"}
          </Button>
        </form>
      </Card>

      <Card className="p-6 bg-card border-border shadow-sm rounded-xl">
        <h3 className="text-xl font-semibold text-foreground mb-4">
          Existing Competencies
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          Drag to reorder competencies
        </p>
        {competencies && <DraggableList items={competencies} onReorder={handleReorder} renderItem={comp => <Badge variant="secondary" className="flex items-center gap-2 px-3 py-2 text-base w-fit bg-primary">
                {comp.competency}
                <button onClick={() => deleteMutation.mutate(comp.id)} className="hover:text-destructive">
                  <Icon name="close" size={16} />
                </button>
              </Badge>} />}
      </Card>
    </div>;
};